/**
 * E2E Key Store - Encrypted storage for X25519 and Ed25519 keypairs
 * Keys are encrypted with a master password using PBKDF2 + AES-GCM
 */

import type { UserId } from '@maco144/shared';
import { generateE2EIdentity, type E2EIdentity } from '@maco144/shared';

/**
 * Encrypted keypair storage
 */
export interface StoredE2EKeys {
  userId: UserId;
  salt: Uint8Array; // For PBKDF2 derivation
  iv: Uint8Array; // For AES-GCM encryption
  encryptedData: Uint8Array; // AES-GCM encrypted E2EIdentity
  createdAt: number;
}

export class E2EKeyStore {
  private dbName = 'rising-sun-e2e-keys';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys', { keyPath: 'userId' });
        }
      };
    });
  }

  private getStore(mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction('keys', mode);
    return tx.objectStore('keys');
  }

  /**
   * Generate new E2E keys and store encrypted with password
   */
  async generateAndStore(userId: UserId, password: string): Promise<E2EIdentity> {
    // Generate fresh keypairs
    const identity = await generateE2EIdentity(userId);

    // Encrypt and store
    await this.storeKeys(userId, identity, password);

    return identity;
  }

  /**
   * Retrieve and decrypt E2E keys with password
   */
  async retrieve(userId: UserId, password: string): Promise<E2EIdentity | null> {
    const stored = await this.getStored(userId);
    if (!stored) return null;

    // Derive key from password
    const derivedKey = await this.deriveKey(password, stored.salt);

    try {
      // Decrypt
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: stored.iv },
        derivedKey,
        stored.encryptedData
      );

      const plaintext = new TextDecoder().decode(decrypted);
      const data = JSON.parse(plaintext);

      // Reconstruct CryptoKey objects from JWK format
      return {
        userId: data.userId,
        encryption: {
          privateKey: await crypto.subtle.importKey(
            'jwk',
            data.encryption.privateKey,
            { name: 'X25519' },
            true,
            ['deriveKey', 'deriveBits']
          ),
          publicKey: await crypto.subtle.importKey(
            'jwk',
            data.encryption.publicKey,
            { name: 'X25519' },
            true,
            ['deriveKey', 'deriveBits']
          ),
        },
        signing: {
          privateKey: await crypto.subtle.importKey(
            'jwk',
            data.signing.privateKey,
            { name: 'Ed25519' },
            true,
            ['sign']
          ),
          publicKey: await crypto.subtle.importKey(
            'jwk',
            data.signing.publicKey,
            { name: 'Ed25519' },
            true,
            ['verify']
          ),
        },
      };
    } catch {
      throw new Error('Failed to decrypt keys - invalid password or corrupted data');
    }
  }

  /**
   * Export keys with new password encryption (for backups)
   */
  async exportKeys(userId: UserId, password: string): Promise<string> {
    const identity = await this.retrieve(userId, password);
    if (!identity) throw new Error(`Keys not found for ${userId}`);

    // Serialize keys to JWK format (exportable)
    const encryptionPrivateJWK = await crypto.subtle.exportKey(
      'jwk',
      identity.encryption.privateKey
    );
    const encryptionPublicJWK = await crypto.subtle.exportKey('jwk', identity.encryption.publicKey);
    const signingPrivateJWK = await crypto.subtle.exportKey('jwk', identity.signing.privateKey);
    const signingPublicJWK = await crypto.subtle.exportKey('jwk', identity.signing.publicKey);

    const data = {
      userId,
      encryption: {
        privateKey: encryptionPrivateJWK,
        publicKey: encryptionPublicJWK,
      },
      signing: {
        privateKey: signingPrivateJWK,
        publicKey: signingPublicJWK,
      },
    };

    // Generate new export encryption
    const exportSalt = crypto.getRandomValues(new Uint8Array(16));
    const exportKey = await this.deriveKey(password, exportSalt);
    const exportIv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: exportIv },
      exportKey,
      new TextEncoder().encode(JSON.stringify(data))
    );

    const exportData = {
      version: 1,
      salt: Array.from(exportSalt),
      iv: Array.from(exportIv),
      encrypted: Array.from(new Uint8Array(encrypted)),
    };

    return JSON.stringify(exportData);
  }

  /**
   * Import keys from encrypted backup
   */
  async importKeys(data: string, password: string): Promise<{ userId: UserId; keys: E2EIdentity }> {
    const exportData = JSON.parse(data);

    if (exportData.version !== 1) {
      throw new Error('Unsupported export version');
    }

    const salt = new Uint8Array(exportData.salt);
    const iv = new Uint8Array(exportData.iv);
    const encrypted = new Uint8Array(exportData.encrypted);

    const key = await this.deriveKey(password, salt);

    try {
      const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);

      const plaintext = new TextDecoder().decode(decrypted);
      const data = JSON.parse(plaintext);

      const keys: E2EIdentity = {
        userId: data.userId,
        encryption: {
          privateKey: await crypto.subtle.importKey(
            'jwk',
            data.encryption.privateKey,
            { name: 'X25519' },
            true,
            ['deriveKey', 'deriveBits']
          ),
          publicKey: await crypto.subtle.importKey(
            'jwk',
            data.encryption.publicKey,
            { name: 'X25519' },
            true,
            ['deriveKey', 'deriveBits']
          ),
        },
        signing: {
          privateKey: await crypto.subtle.importKey(
            'jwk',
            data.signing.privateKey,
            { name: 'Ed25519' },
            true,
            ['sign']
          ),
          publicKey: await crypto.subtle.importKey(
            'jwk',
            data.signing.publicKey,
            { name: 'Ed25519' },
            true,
            ['verify']
          ),
        },
      };

      return {
        userId: data.userId,
        keys,
      };
    } catch {
      throw new Error('Failed to import keys - invalid password or corrupted data');
    }
  }

  /**
   * Delete keys for a user
   */
  async delete(userId: UserId): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').delete(userId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all keys (for testing)
   */
  async clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Close database
   */
  close(): void {
    this.db?.close();
    this.db = null;
  }

  // ============ Private Helpers ============

  private async storeKeys(userId: UserId, identity: E2EIdentity, password: string): Promise<void> {
    // Export keys to JWK format for storage
    const encryptionPrivateJWK = await crypto.subtle.exportKey(
      'jwk',
      identity.encryption.privateKey
    );
    const encryptionPublicJWK = await crypto.subtle.exportKey('jwk', identity.encryption.publicKey);
    const signingPrivateJWK = await crypto.subtle.exportKey('jwk', identity.signing.privateKey);
    const signingPublicJWK = await crypto.subtle.exportKey('jwk', identity.signing.publicKey);

    const data = {
      userId,
      encryption: {
        privateKey: encryptionPrivateJWK,
        publicKey: encryptionPublicJWK,
      },
      signing: {
        privateKey: signingPrivateJWK,
        publicKey: signingPublicJWK,
      },
    };

    // Encrypt with password
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.deriveKey(password, salt);

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(data))
    );

    const stored: StoredE2EKeys = {
      userId,
      salt,
      iv,
      encryptedData: new Uint8Array(encrypted),
      createdAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = this.getStore('readwrite').put(stored);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async getStored(userId: UserId): Promise<StoredE2EKeys | null> {
    return new Promise((resolve, reject) => {
      const request = this.getStore('readonly').get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as ArrayBuffer,
        iterations: 100000,
        hash: 'SHA-256',
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }
}
